import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const MARKETPLACE_PLATFORMS = [
  { value: 'amazon', label: 'Amazon' },
  { value: 'aliexpress', label: 'AliExpress' },
  { value: 'temu', label: 'Temu' },
  { value: 'shein', label: 'Shein' },
  { value: 'magento', label: 'Magento 2' },
  { value: 'woocommerce', label: 'WooCommerce' },
  { value: 'tiktok', label: 'TikTok Shop' },
  { value: 'instagram', label: 'Instagram Shopping' },
  { value: 'facebook', label: 'Facebook Shopping' }
];

const MarketplaceIntegrationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    display_name: '',
    platform: 'amazon',
    integration_type: 'marketplace',
    is_active: true,
    credentials: {}
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditMode) {
      loadIntegration();
    }
  }, [id]);

  const loadIntegration = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/integrations/${id}`);
      const integration = response.data;
      
      setFormData({
        display_name: integration.display_name || '',
        platform: integration.platform || 'amazon',
        integration_type: 'marketplace',
        is_active: integration.is_active !== undefined ? integration.is_active : true,
        credentials: integration.credentials || {}
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load integration');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      const payload = {
        display_name: formData.display_name,
        platform: formData.platform,
        integration_type: 'marketplace',
        is_active: formData.is_active,
        credentials: formData.credentials,
        configuration: {
          marketplace_type: formData.platform
        }
      };

      if (isEditMode) {
        await api.put(`/integrations/${id}`, payload);
      } else {
        await api.post('/integrations', payload);
      }

      navigate('/integrations');
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'create'} integration`);
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [field]: value
      }
    }));
  };

  const renderCredentialFields = () => {
    const { platform } = formData;

    switch (platform) {
      case 'amazon':
        return (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Seller ID *</label>
              <input
                type="text"
                value={formData.credentials.seller_id || ''}
                onChange={(e) => handleCredentialChange('seller_id', e.target.value)}
                required
              />
            </div>
            <div>
              <label style={styles.label}>Access Key *</label>
              <input
                type="text"
                value={formData.credentials.access_key || ''}
                onChange={(e) => handleCredentialChange('access_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Secret Key *</label>
              <input
                type="password"
                value={formData.credentials.secret_key || ''}
                onChange={(e) => handleCredentialChange('secret_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Marketplace ID</label>
              <input
                type="text"
                value={formData.credentials.marketplace_id || ''}
                onChange={(e) => handleCredentialChange('marketplace_id', e.target.value)}

                placeholder="e.g., ATVPDKIKX0DER"
              />
            </div>
            <div>
              <label style={styles.label}>Region</label>
              <select
                value={formData.credentials.region || 'us-east-1'}
                onChange={(e) => handleCredentialChange('region', e.target.value)}

              >
                <option value="us-east-1">North America</option>
                <option value="eu-west-1">Europe</option>
                <option value="us-west-2">Far East</option>
              </select>
            </div>
          </>
        );

      case 'aliexpress':
        return (
          <>
            <div>
              <label style={styles.label}>App Key *</label>
              <input
                type="text"
                value={formData.credentials.app_key || ''}
                onChange={(e) => handleCredentialChange('app_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>App Secret *</label>
              <input
                type="password"
                value={formData.credentials.app_secret || ''}
                onChange={(e) => handleCredentialChange('app_secret', e.target.value)}

                required
              />
            </div>
          </>
        );

      case 'temu':
        return (
          <>
            <div>
              <label style={styles.label}>API Key *</label>
              <input
                type="password"
                value={formData.credentials.api_key || ''}
                onChange={(e) => handleCredentialChange('api_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Shop ID *</label>
              <input
                type="text"
                value={formData.credentials.shop_id || ''}
                onChange={(e) => handleCredentialChange('shop_id', e.target.value)}

                required
              />
            </div>
          </>
        );

      case 'shein':
        return (
          <>
            <div>
              <label style={styles.label}>API Key *</label>
              <input
                type="password"
                value={formData.credentials.api_key || ''}
                onChange={(e) => handleCredentialChange('api_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Merchant ID *</label>
              <input
                type="text"
                value={formData.credentials.merchant_id || ''}
                onChange={(e) => handleCredentialChange('merchant_id', e.target.value)}

                required
              />
            </div>
          </>
        );

      case 'magento':
        return (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Base URL *</label>
              <input
                type="url"
                value={formData.credentials.base_url || ''}
                onChange={(e) => handleCredentialChange('base_url', e.target.value)}

                placeholder="https://your-store.com"
                required
              />
            </div>
            <div>
              <label style={styles.label}>Access Token *</label>
              <input
                type="password"
                value={formData.credentials.access_token || ''}
                onChange={(e) => handleCredentialChange('access_token', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Store Code</label>
              <input
                type="text"
                value={formData.credentials.store_code || ''}
                onChange={(e) => handleCredentialChange('store_code', e.target.value)}

                placeholder="default"
              />
            </div>
          </>
        );

      case 'woocommerce':
        return (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Base URL *</label>
              <input
                type="url"
                value={formData.credentials.base_url || ''}
                onChange={(e) => handleCredentialChange('base_url', e.target.value)}

                placeholder="https://your-store.com"
                required
              />
            </div>
            <div>
              <label style={styles.label}>Consumer Key *</label>
              <input
                type="text"
                value={formData.credentials.consumer_key || ''}
                onChange={(e) => handleCredentialChange('consumer_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Consumer Secret *</label>
              <input
                type="password"
                value={formData.credentials.consumer_secret || ''}
                onChange={(e) => handleCredentialChange('consumer_secret', e.target.value)}

                required
              />
            </div>
          </>
        );

      case 'tiktok':
        return (
          <>
            <div>
              <label style={styles.label}>App Key *</label>
              <input
                type="text"
                value={formData.credentials.app_key || ''}
                onChange={(e) => handleCredentialChange('app_key', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>App Secret *</label>
              <input
                type="password"
                value={formData.credentials.app_secret || ''}
                onChange={(e) => handleCredentialChange('app_secret', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Shop ID *</label>
              <input
                type="text"
                value={formData.credentials.shop_id || ''}
                onChange={(e) => handleCredentialChange('shop_id', e.target.value)}

                required
              />
            </div>
          </>
        );

      case 'instagram':
        return (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Access Token *</label>
              <input
                type="password"
                value={formData.credentials.access_token || ''}
                onChange={(e) => handleCredentialChange('access_token', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Business Account ID *</label>
              <input
                type="text"
                value={formData.credentials.business_account_id || ''}
                onChange={(e) => handleCredentialChange('business_account_id', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Catalog ID</label>
              <input
                type="text"
                value={formData.credentials.catalog_id || ''}
                onChange={(e) => handleCredentialChange('catalog_id', e.target.value)}

                placeholder="Optional"
              />
            </div>
          </>
        );

      case 'facebook':
        return (
          <>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Access Token *</label>
              <input
                type="password"
                value={formData.credentials.access_token || ''}
                onChange={(e) => handleCredentialChange('access_token', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Page ID *</label>
              <input
                type="text"
                value={formData.credentials.page_id || ''}
                onChange={(e) => handleCredentialChange('page_id', e.target.value)}

                required
              />
            </div>
            <div>
              <label style={styles.label}>Catalog ID *</label>
              <input
                type="text"
                value={formData.credentials.catalog_id || ''}
                onChange={(e) => handleCredentialChange('catalog_id', e.target.value)}

                required
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate('/integrations')}
          className="breadcrumb-link"
        >
          Integrations
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditMode ? 'Edit Marketplace' : 'New Marketplace'}
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          ...styles.header,
          borderBottom: '3px solid #FF6B6B'
        }}>
          <div>
            <h2 style={styles.title}>
              {isEditMode ? 'Edit Marketplace Integration' : 'New Marketplace Integration'}
            </h2>
            <p style={styles.subtitle}>
              Configure your marketplace platform credentials
            </p>
          </div>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Display Name *</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}

                placeholder="e.g., My Amazon Store"
                required
              />
            </div>

            <div>
              <label style={styles.label}>Platform *</label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  platform: e.target.value,
                  credentials: {} // Reset credentials when platform changes
                }))}

                required
              >
                {MARKETPLACE_PLATFORMS.map(platform => (
                  <option key={platform.value} value={platform.value}>
                    {platform.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Credentials Section */}
          <div style={{ marginTop: '2rem' }}>
            <h3 style={styles.sectionTitle}>Platform Credentials</h3>
            <div style={styles.formGrid}>
              {renderCredentialFields()}
            </div>
          </div>

          {/* Status */}
          <div style={{ marginTop: '2rem' }}>
            <label style={{ ...styles.label, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                style={{ marginRight: '0.5rem' }}
              />
              Active
            </label>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              style={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={styles.submitButton}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Integration' : 'Create Integration')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1360px',
    margin: '0 auto',
    padding: '2rem'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '1.5rem',
    fontSize: '0.875rem'
  },
  breadcrumbLink: {
    background: 'none',
    border: 'none',
    color: 'var(--primary-600)',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'none'
  },
  header: {
    paddingBottom: '1.5rem',
    marginBottom: '1.5rem'
  },
  title: {
    color: 'var(--gray-900)',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: '0 0 0.25rem 0'
  },
  subtitle: {
    color: 'var(--gray-600)',
    margin: 0,
    fontSize: '0.875rem'
  },
  errorMessage: {
    background: '#fee',
    border: '1px solid #fcc',
    color: '#c33',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1.5rem'
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--gray-700)',
    fontWeight: '500',
    fontSize: '0.875rem'
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: 'var(--gray-800)',
    marginBottom: '1rem'
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid var(--gray-200)'
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    border: '1px solid var(--gray-300)',
    borderRadius: '8px',
    background: 'white',
    color: 'var(--gray-700)',
    cursor: 'pointer',
    fontWeight: '500'
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    background: '#FF6B6B',
    color: 'white',
    cursor: 'pointer',
    fontWeight: '500'
  }
};

export default MarketplaceIntegrationForm;
